import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../utils/api";
import { BASE_URLS } from "../../config/urls";
import { ModalShell } from "./ModalShell";
import { Company } from "../../models/Company";
import { BtnSpinner } from "../fields/BtnSpinner";

interface CompanyResolveResponse {
    inn: string;
    foundCompany: boolean;
    selfEmployed: boolean;
    name: string | null;
    fio: string | null;
    message: string | null;
}

interface CompaniesModalProps {
    isOpen: boolean;
    onClose: () => void;
    autoOpened: boolean;

    companies: any[];
    onCompaniesChanged: () => Promise<void> | void;
}

export function CompaniesModal({
                                   isOpen,
                                   onClose,
                                   autoOpened,
                                   companies,
                                   onCompaniesChanged,
                               }: CompaniesModalProps) {
    const [loadingList, setLoadingList] = useState(false);

    const [inn, setInn] = useState("");
    const [innError, setInnError] = useState("");

    const [fio, setFio] = useState("");
    const [fioError, setFioError] = useState("");

    const [name, setName] = useState("");
    const [isSelfEmployed, setIsSelfEmployed] = useState(false);

    const [saving, setSaving] = useState(false);
    const [searchingInn, setSearchingInn] = useState(false);

    const [err, setErr] = useState("");
    const [ok, setOk] = useState("");

    // анти-гонки + отмена fetch
    const reqSeq = useRef(0);
    const abortRef = useRef<AbortController | null>(null);

    // debounce timer
    const debounceRef = useRef<number | null>(null);

    const canAdd = useMemo(() => {
        if (saving) return false;
        if (!name.trim()) return false;
        if (!/^\d{10}(\d{2})?$/.test(inn)) return false;

        if (isSelfEmployed) {
            if (inn.length !== 12) return false;
            if (!fio.trim() || !fio.includes(" ")) return false;
        }

        return true;
    }, [saving, name, inn, isSelfEmployed, fio]);

    useEffect(() => {
        if (isOpen) {
            setInn("");
            setInnError("");
            setFio("");
            setFioError("");
            setName("");
            setIsSelfEmployed(false);
            setErr("");
            setOk("");
            setSaving(false);
            setSearchingInn(false);

            reqSeq.current = 0;

            if (abortRef.current) abortRef.current.abort();
            abortRef.current = null;

            if (debounceRef.current) window.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, [isOpen]);

    useEffect(() => {
        // каждый раз при изменении inn — чистим статусы/ошибки и "сбрасываем" прошлые запросы
        setInnError("");
        setOk("");
        setErr("");

        // пока не 10/12 — чистим найденное и не ищем
        if (inn.length !== 10 && inn.length !== 12) {
            setSearchingInn(false);
            setName("");
            setIsSelfEmployed(false);
            setFio("");
            setFioError("");

            // отмена старого запроса и debounce
            reqSeq.current++;
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = null;

            if (debounceRef.current) window.clearTimeout(debounceRef.current);
            debounceRef.current = null;

            return;
        }

        // debounce: ждём 350мс после последнего ввода
        if (debounceRef.current) window.clearTimeout(debounceRef.current);

        debounceRef.current = window.setTimeout(async () => {
            const seq = ++reqSeq.current;

            // отменяем предыдущий fetch
            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            // reset before search
            setSearchingInn(true);
            setName("");
            setIsSelfEmployed(false);
            setFio("");
            setFioError("");

            try {
                const resp = await apiFetch(
                    `${BASE_URLS.api}/companies/resolve?inn=${encodeURIComponent(inn)}`,
                    {
                        method: "GET",
                        signal: controller.signal,
                        headers: { "Content-Type": "application/json" },
                    } as any // если твой apiFetch типизирован без signal — можно убрать any и расширить типы
                );

                if (!resp.ok) throw new Error("Не удалось проверить ИНН");

                const data = (await resp.json()) as CompanyResolveResponse;

                // если уже был новый запрос — игнорим
                if (seq !== reqSeq.current) return;

                if (data.selfEmployed) {
                    setIsSelfEmployed(true);
                    setInnError("Найден самозанятый");

                    if (data.fio) {
                        setFio(String(data.fio));
                        setName(String(data.fio));
                        setFioError("");
                    } else {
                        setFio("");
                        setName("");
                    }
                    return;
                }

                if (data.foundCompany && data.name) {
                    setIsSelfEmployed(false);
                    setInnError("");
                    setName(String(data.name));
                    return;
                }

                // не найдено
                setIsSelfEmployed(false);
                setName("");
                setInnError(data.message || "Такая компания не найдена");
            } catch (e: any) {
                // AbortError — норм, просто отмена
                if (e?.name === "AbortError") return;

                if (seq !== reqSeq.current) return;
                setIsSelfEmployed(false);
                setName("");
                setInnError(e?.message || "Ошибка при проверке ИНН");
            } finally {
                if (seq !== reqSeq.current) return;
                setSearchingInn(false);
            }
        }, 350);

        return () => {
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
        };
    }, [inn]);

    if (!isOpen) return null;

    function handleInnInput(e: React.ChangeEvent<HTMLInputElement>) {
        let inputValue = e.target.value;

        // только цифры, максимум 12
        const lastChar = inputValue.charAt(inputValue.length - 1);
        if (lastChar && (isNaN(Number(lastChar)) || lastChar === " " || inputValue.length === 13)) {
            e.target.value = inputValue.slice(0, -1);
            return;
        }

        setInn(inputValue);
    }

    function handleFioInput(e: React.ChangeEvent<HTMLInputElement>) {
        let inputValue = e.target.value;

        const lastChar = inputValue.charAt(inputValue.length - 1);
        if (lastChar && !/^[a-zA-Zа-яА-ЯёЁ ]$/.test(lastChar)) {
            e.target.value = inputValue.slice(0, -1);
            return;
        }

        setFio(inputValue);
        setFioError("");

        if (inputValue.trim()) {
            setName(inputValue.trim());
        } else {
            setName("");
            setFioError("Необходимо ввести ФИО");
        }
    }

    function checkInn() {
        if (!/^\d{10}(\d{2})?$/.test(inn)) {
            setInnError("ИНН должен состоять из 10 или 12 цифр");
            return false;
        }
        return true;
    }

    function checkSelfEmployed() {
        if (isSelfEmployed) {
            if (inn.length !== 12) {
                setInnError("ИНН должен состоять из 12 цифр");
                return false;
            }
            if (!fio.includes(" ")) {
                setFioError("Введите корректный ФИО");
                return false;
            }
        }
        return true;
    }

    const addInn = async () => {
        setErr("");
        setOk("");

        const isInnCorrect = checkInn();
        const isSelfEmployedCorrect = checkSelfEmployed();
        if (!isInnCorrect || !isSelfEmployedCorrect || name.trim() === "") return;

        setSaving(true);
        try {
            const payload: Company = { companyName: name.trim(), inn: inn.trim() };

            const resp = await apiFetch(`${BASE_URLS.api}/companies/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) throw new Error("Не удалось добавить компанию");

            setOk("Компания добавлена");
            await onCompaniesChanged();

            setInn("");
            setInnError("");
            setFio("");
            setFioError("");
            setName("");
            setIsSelfEmployed(false);
            setSearchingInn(false);

            reqSeq.current = 0;
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = null;
        } catch (e: any) {
            setErr(e?.message || "Ошибка добавления компании");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCompany = async (companyInn: string) => {
        setErr("");
        setOk("");

        try {
            const resp = await apiFetch(`${BASE_URLS.api}/companies/delete/${companyInn}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });

            if (!resp.ok) throw new Error("Ошибка при удалении компании");

            setOk("Компания удалена");
            await onCompaniesChanged();
        } catch (e: any) {
            setErr(e?.message || "Ошибка удаления");
        }
    };

    return (
        <ModalShell
            title="Компании"
            subtitle={autoOpened ? "Чтобы продолжить работу, добавьте компанию" : "Управление компаниями"}
            onClose={onClose}
        >
            {err && <div className="profile-error">{err}</div>}
            {ok && <div className="profile-success">{ok}</div>}

            {/* ===== current companies ===== */}
            <div className="profile-companies-block">
                <div className="profile-companies-head">
                    <div className="profile-companies-title">Текущие компании</div>
                </div>

                {loadingList && (
                    <div className="profile-muted">
                        <div className="page_content">
                            <div className="spinner-wrapper" style={{ marginTop: 80 }}>
                                <div className="spinner"></div>
                            </div>
                        </div>
                    </div>
                )}

                {!loadingList && companies.length === 0 && <div className="profile-muted">Компаний пока нет.</div>}

                {!loadingList && companies.length > 0 && (
                    <div className="profile-companies-list">
                        {companies.map((c: any) => {
                            const cInn = String(c.inn ?? "");
                            const companyName = c.companyName ?? "Без названия";

                            return (
                                <div key={cInn || companyName} className="profile-company-row">
                                    <div className="profile-company-main">
                                        <div className="profile-company-name">{companyName}</div>
                                        {!!cInn && <div className="profile-company-sub">ИНН: {cInn}</div>}
                                    </div>

                                    {!!cInn && (
                                        <button
                                            type="button"
                                            className="profile-mini-danger"
                                            onClick={() => handleDeleteCompany(cInn)}
                                            disabled={saving}
                                            title="Удалить"
                                        >
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ===== add company by INN ===== */}
            <div className="profile-divider" />

            <div className="profile-companies-title">Добавить компанию по ИНН</div>

            <div className="profile-modal-grid profile-modal-grid--one">
                <div className="profile-field">
                    <label className="profile-label">ИНН</label>

                    <div className="profile-input-with-spinner">
                        <input
                            className="profile-input"
                            value={inn}
                            onChange={handleInnInput}
                            placeholder="10 или 12 цифр"
                            inputMode="numeric"
                        />
                        {searchingInn && <span className="mini-spinner" aria-label="Поиск" />}
                    </div>

                    {innError && <div className="profile-inline-hint profile-inline-hint--bad">{innError}</div>}

                    {!innError && name && !searchingInn && (
                        <div className="profile-inline-hint profile-inline-hint--ok">Найдено: {name}</div>
                    )}

                    {!innError && !name && searchingInn && <div className="profile-inline-hint">Идет поиск…</div>}
                </div>

                {isSelfEmployed && (
                    <div className="profile-field">
                        <label className="profile-label">ФИО (для самозанятого)</label>
                        <input
                            className="profile-input"
                            value={fio}
                            onChange={handleFioInput}
                            placeholder="Иванов Иван Иванович"
                        />
                        {fioError && <div className="profile-inline-hint profile-inline-hint--bad">{fioError}</div>}
                    </div>
                )}
            </div>

            <div className="profile-modal-actions">
                <button className="profile-secondary-btn" onClick={onClose} disabled={saving}>
                    Закрыть
                </button>

                <button className="profile-primary-btn" onClick={addInn} disabled={!canAdd}>
                    {saving ? <BtnSpinner /> : "Добавить"}
                </button>
            </div>
        </ModalShell>
    );
}
